import Pagination   from './Listing/Pagination.es6';
import Slider       from './Listing/Slider.es6';
import Title        from './Listing/Title.es6';
// Types
import Box          from "./Listing/Type/Box.es6";
import Teaser       from "./Listing/Type/Teaser.es6";

(() => {
    class Listing extends App.Component {
        constructor(container) {
            super(container);
            this.buttons            = this.read('buttons').split(',');
            this.items              = [];
            this.transition         = {
                duration:           100,
            };
            this.refreshTime        = 30;
            this.rendered           = {};

            let products = this.read('products').split(',') || [];
            products.forEach(productID => this.addProduct(productID));

            /* CSS Properties */
            this.container.style.setProperty('--box-listing-transition-timing', this.transition.duration + 'ms');

            this.addRenderBlockingPromises([
                App.Endpoint.Products.promise(),
                App.Endpoint.Offers.promise(),
                App.Endpoint.Badges.get,
            ]);

            this.bind();
            this.update();
        }

        /* Constants */
        get Title()         { return this._title || (this._title = new Title(this)) }
        get Pagination()    { return this._pagination || (this._pagination = new Pagination(this)) }
        get Slider()        { return this._slider || (this._slider = new Slider(this)) }
        get SliderComponent() { return this.Slider?.container?.closest('.component').getComponent('Slider') }
        get navigation()    { return this.container.querySelector('.box-listing-navigation') }
        get wrap()          { return this.container.querySelector('.box-listing-wrap') }
        get quickBuy()      { return this._quickBuy || (this._quickBuy = (this.container.classList.contains('js-product-directly'))) }
        get dynamically()   { return this._dynamically || (this._dynamically = this.read('dynamically').split()) }
        get keepExistingContent() { return this._keepExistingContent || (this._keepExistingContent = (this.container.classList.contains('keep-existing-content'))) }

        /* Getters */
        get layout()        { return this.container.dataset.layout }
        get limit ()        { return this.container.dataset.limit || 0 }
        get width()         { return this._width = this.container.clientWidth }
        get grid()          {
            switch (App.Interface.Breakpoint.getFromWidth(this.width)) {
                case 'xs':
                case 'sm':
                    return 'col-xs-6';
                case 'md':
                    return 'col-md-4';
                case 'lg':
                case 'xl':
                    return 'col-lg-3';
            }
        }
        get showcase() {
            if (!this._showcase) {
                switch (this.type) {
                    case 'box':
                        return (this._showcase = new Box(this));
                    case 'teaser':
                        return (this._showcase = new Teaser(this));
                }
            }
            return this._showcase;
        }
        get type()          { return this.container.dataset.type }

        /* Getters & Setters */
        get height()        { return this.container.clientHeight }
        set height(height)  { this.container.style.setProperty('--box-listing-height', height + 'px') }

        isLayout(layout) {
            return layout === this.layout;
        }

        hasReachMax() {
            return this.isLayout('pagination') ?
                (this.items.length >= this.Pagination.max +1) : false;
        }

        addProduct(id) {
            // render product
            if (id !== '' && this.items.indexOf(id) === -1) {
                App.Interface.Api.findById('products', id, product => {
                    this.items.push(product.id);
                    this.renderTimeOut();
                });
            }
        }

        getResult() {
            const hasLimit = this.limit > 0;

            switch (this.layout) {
                case 'pagination':
                    const pagination  = this.Pagination;
                    const pages       = this.getPages() -1;
                    const limitPages  = hasLimit ? Math.ceil(this.limit / pagination.max) - 1 : pages;

                    /* Change offset if less than 0 */
                    if (pagination.offset < 0) pagination.offset = limitPages < 0 ? 0 : limitPages;

                    /* Reset offset if less page doesn't exists */
                    if (pagination.offset > limitPages) pagination.offset = 0;

                    let start = pagination.offset * pagination.max;

                    /* Return first page if 0 */
                    if (pagination.offset === 0) {
                        return this.items.slice(0, (hasLimit && this.limit < pagination.max) ? this.limit : pagination.max);
                    }

                    /* Return result */
                    return this.items.slice(start, ((hasLimit && this.limit <= start + pagination.max) ? this.limit : start + pagination.max));
                default:
                    return hasLimit ?
                        this.items.slice(0, this.limit) : this.items;
            }
        }

        getPages() {
            return Math.ceil(this.items.length / this.Pagination.max);
        }

        renderTimeOut() {
            clearTimeout(this.timeout);
            if (!this.hasReachMax()) {
                this.timeout = setTimeout(() => this.update(), this.refreshTime);
            }
        }

        async product(id) {
            // Replace with "getById" when this also async
            await App.Endpoint.Products.promise();
            let product = App.Endpoint.Products.getById(id);
            if (product) {
                let classes = [];
                if (this.container.classList.contains('show-hints')) classes.push('show-hints');
                product.class = classes.join(' ');
            }
            if (product.type === 0 && product.availability === 0) {
                product.filteredButtons = this.buttons.filter((button) => button !== 'cart');
            }
            return product;
        }

        wrapper(item, key) {
            switch (this.layout) {
                case 'list':
                    return App.Template.parse('box-listing-item', {
                        grid:       this.grid,
                        content:    item,
                    });
                case 'slider':
                    return App.Template.parse('component-slider-item', {
                        content: item,
                        key: key
                    });
                default:
                    return App.Template.parse('grid-item', item);
            }
        }

        async renderItem(product, key) {
            // Cache
            if (!this.rendered[product.id]) {
                this.rendered[product.id] = this.wrapper(await this.showcase.render(product), key);
            }
            return this.rendered[product.id];
        }

        render() {
            let result  = this.getResult();
            result = result.filter((item, index) => result.indexOf(item) === index);
            // Do not render if result has not change
            if (this._lastResult !== undefined && JSON.stringify(result) === this._lastResult)
                return this;
            this._lastResult = JSON.stringify(result);

            // start rendering
            const wait = [];
            for (let id of result) wait.push(this.product(id));
            Promise.all(wait).then(async products => {
                let content = '';
                let key = 0;
                for (let product of products) {
                    product = {...product}; // clone object
                    if (product) content += await this.renderItem(product, key);
                    key++;
                }
                if (!this.keepExistingContent) this.setContent(content);
                this.equalizer();
            });
            return this;
        }

        setButtons(buttons) {
            this.buttons = buttons;
        }

        setContent(content) {
            this.wrap.innerHTML = content;
            if (this.wrap.Grid) this.wrap.Grid.update();
            this.updateLayout();
        }

        calculateHeight() {
            /* Overflow troubles Chrome, please remove this when Chrome updates */
            if (this.isEmpty()) {
                this.container.style.setProperty('--box-listing-height', this.container.style.height + 'px');
            } else {
                this.container.style.setProperty('--box-listing-height', this.height + 'px');
            }
        }

        updatePagination() {
            let width       = this.navigation.offsetWidth;
            let size        = width / this.getPages();
            let position    = size * (this.Pagination.offset);
            this.container.style.setProperty('--box-listing-navigation-position', position + 'px');
            this.container.style.setProperty('--box-listing-navigation-size', size + 'px');
            this.togglePagination();
        }

        isEmpty() {
            return (this.wrap.innerHTML === '');
        }

        empty() {
            this.wrap.innerHTML = '';
            return this;
        }

        reset() {
            this.items = [];
        }

        changePage(actual) {
            this.height = this.height; // stamp actual height
            let classList = this.container.classList;
            classList.add('intersection');
            let pri = actual < this.Pagination.offset ? 'next' : 'prev';
            let sec = actual < this.Pagination.offset ? 'prev' : 'next';
            classList.add(pri);
            setTimeout(() => {
                classList.remove(pri);
                classList.add(sec);
                this.render();
                setTimeout(() => {
                    classList.remove(sec);
                    setTimeout(() => classList.remove('intersection'), this.refreshTime * 10);
                }, this.refreshTime * 10);
            }, this.refreshTime);
        }

        nextPage() {
            switch (this.layout) {
                case 'pagination':
                    this.changePage(this.Pagination.offset++);
                    break;
                case 'slider':
                    this.Slider.scrollNext();
                    break;
            }
            App.Components.apply();
            return this;
        }

        previousPage() {
            switch (this.layout) {
                case 'pagination':
                    this.changePage(this.Pagination.offset--);
                    break;
                case 'slider':
                    this.Slider.scrollPrev();
                    break;
            }
            App.Components.apply();
            return this;
        }

        bind() {
            $(this.container).on('click', '.js-prev', event => this.previousPage());
            $(this.container).on('click', '.js-next', event => this.nextPage());
            window.addEventListener('resize', event => this.update());
            if (this.layout === 'slider') this.Slider.bind();
        }

        togglePagination() {
            if (this.items.length <= this.Pagination.max) {
                this.container.querySelectorAll('.listing-pag-button').forEach(e => e.classList.add('hidden'));
            } else {
                this.container.querySelectorAll('.listing-pag-button').forEach(e => e.classList.remove('hidden'));
            }
        }

        updateTitle(title) {
            // @todo: move this to this.Title.update();
            if (title !== undefined && this.Title.container) {
                this.Title.container.innerHTML = title;
            }

            if (this.Title.wrap) {
                if (this.showTitle) {
                    this.Title.wrap.classList.add('hidden');
                } else {
                    this.Title.wrap.classList.remove('hidden');
                }
            }
            this.Title.update();
        }

        get showTitle() {
            return !this.Title.container
                && !this.isLayout('pagination') && !this.isLayout('slider');
        }

        updateLayout() {
            this.calculateHeight();
            this.updateTitle();
            if (this.isLayout('pagination')) this.updatePagination();
            App.Layout.refresh();
            setTimeout(() => {
                if (this.container.querySelectorAll('.box')) {
                    this.container.querySelectorAll('.box').forEach(box => {
                        let id = box.dataset.id;
                        if (id) this.rendered[id] = box.outerHTML;
                    });
                }
            }, 2000);
        }

        // equalize properties to all boxes
        equalizer() {
            // equalize description container
            let descriptionContainer = this.container.querySelectorAll('.box .description');
            let ifOneContainerHasDescription = false;

            if (descriptionContainer) {
                descriptionContainer.forEach(container => {
                    if (container.textContent !== '') ifOneContainerHasDescription = true;
                });
                if (ifOneContainerHasDescription) {
                    descriptionContainer.forEach(container => {
                        if (container.innerHTML === '') container.innerHTML = ' '; // this will show hidden container
                    });
                }
            }
        }

        update() {
            super.update(() => {
                /* If pagination is active it will show the FMP as soon as enough items */
                if (this.isLayout('pagination')) {
                    /* Kill old interval */
                    clearInterval(this.updateInterval);
                    this.updateInterval = setInterval(() => {
                        if (this.hasReachMax()) {
                            this.render();
                            /* Kill render running processes */
                            clearInterval(this.updateInterval);
                            clearTimeout(this.updateTime);
                        }
                    }, this.refreshTime);
                    this.updateTime = setTimeout(() => {
                        clearInterval(this.updateInterval);
                        // render with given items (no error)
                        this.render();
                    }, this.refreshTime * 3);
                } else {
                    /* Render directly without delay */
                    this.render();

                    if (this.SliderComponent) {
                        setTimeout(() => {
                            this.SliderComponent.update();
                        }, this.refreshTime);
                    }
                }
                this.Title.update();
            });
        }
    }
    App.Components.register('.component.box-listing', Listing, 'Listing');
})();

